/**
 * PGSS-5750: Scan to load a customer shipment. Pure helpers for the screen and for Mark as shipped, so their rules are
 * tested without rendering anything.
 *
 * The server decides everything about a scan (GET /api/customer-shipments/{id}/load, POST .../load/scan); this module
 * only shows what it answered. Only LOADED records a box. The offline queue, code cleaning and colours are the
 * supplier Scan to load's (PGSS-5748), reused from scanToLoadRules.
 */
import { REQUEST_TIMEOUT_MS, TONE_COLORS, displayCode } from 'app/main/apps/scanToLoad/scanToLoadRules';

/** Results the server answers, plus two of the screen's own: QUEUED (saved offline) and ERROR (the request failed). */
export const SHIPMENT_SCAN_RESULTS = {
  LOADED: { tone: 'success', label: 'Loaded' },
  ALREADY_LOADED: { tone: 'neutral', label: 'Already scanned' },
  NOT_ON_THIS_SHIPMENT: { tone: 'error', label: 'Not on this shipment' },
  OUT_OF_DATE: { tone: 'warning', label: 'Label out of date' },
  UNKNOWN_CODE: { tone: 'error', label: 'Unknown code' },
  NOT_A_BOX_CODE: { tone: 'warning', label: 'Not a box code' },
  SHIPMENT_CLOSED: { tone: 'warning', label: 'Already shipped' },
  QUEUED: { tone: 'info', label: 'Saved, waiting to send' },
  ERROR: { tone: 'error', label: 'Not sent' },
};

/** How a result is shown. An unknown result reads as an error, never as a success. */
export function shipmentScanResultView(result) {
  const known = SHIPMENT_SCAN_RESULTS[result] || SHIPMENT_SCAN_RESULTS.ERROR;
  return { ...known, ...TONE_COLORS[known.tone] };
}

/** The screen's route. */
export function shipmentLoadPath(shipmentId) {
  return `/shipments/load/${shipmentId}`;
}

/** The API base for one shipment's load. */
export function shipmentLoadApi(shipmentId) {
  return `/api/customer-shipments/${shipmentId}/load`;
}

/**
 * The offline queue's id for a customer shipment. The supplier screen keys its queue by shipment id alone; this one
 * adds a prefix so the two screens never share a queue.
 */
export function shipmentQueueId(shipmentId) {
  return `customer-shipment.${shipmentId}`;
}

/** PSG staff only: a refused scan names another order's customer, so suppliers and customers never get the screen. */
const LOAD_ROLES = ['admin', 'super admin', 'staff', 'sales', 'warehouse employee', 'warehouse lead'];

/**
 * A customer shipment already marked shipped, by its status CODE: the status's name is editable (PGSS-5537), so a
 * renamed "Completed & Shipped" must not bring back Scan to load. The name is only used when no code came with it.
 */
export const SHIPPED_STATUS_CODE = 'ORDER_COMPLETED_AND_SHIPPED';
export function isShipmentShipped(status) {
  if (status?.system_code) return status.system_code === SHIPPED_STATUS_CODE;
  return status?.name === 'Completed & Shipped';
}

/**
 * PGSS-5875: past Completed & Shipped a shipment can be Invoiced, Partly paid or Paid in full, and the server refuses
 * every scan on all of them (CustomerShipmentLoadService.CLOSED_STATUS_CODES). Scan to load is offered only before
 * that. Not isShipmentShipped: Send packing slip, Mark as shipped and the VAS warning still key off shipped alone.
 */
const CLOSED_FOR_LOADING_CODES = [
  SHIPPED_STATUS_CODE,
  'ORDER_INVOICED',
  'ORDER_PARTIAL_PAYMENT',
  'ORDER_COMPLETED_AND_PAID_IN_FULL',
];
export function isShipmentClosedForLoading(status) {
  if (status?.system_code) return CLOSED_FOR_LOADING_CODES.includes(status.system_code);
  return isShipmentShipped(status);
}

/**
 * Mark as shipped was refused because boxes are still to load (a 400 from the server): the screen then asks for the
 * confirmation instead of just closing. Any other failure is shown as before.
 */
export function isStillToLoadRefusal(action) {
  return Boolean(action?.error) && action?.payload?.status === 400;
}

export function canUseShipmentLoad(role) {
  return LOAD_ROLES.includes(String(role || '').toLowerCase());
}

/** The orders of the load, each with only its boxes and lines still to load (or only those loaded). */
function pick(view, wantLoaded) {
  const orders = Array.isArray(view?.orders) ? view.orders : [];
  return orders
    .map((order) => ({
      ...order,
      boxes: (order.boxes || []).filter((box) => !!box?.loaded === wantLoaded),
      items: (order.items || []).filter((item) => !!item?.ticked === wantLoaded),
    }))
    .filter((order) => order.boxes.length > 0 || order.items.length > 0);
}

/** "Still to load" and "Loaded", each grouped by order, in the server's order. */
export function splitLoad(view) {
  return { stillToLoad: pick(view, false), loaded: pick(view, true) };
}

/** What is still to load, one line each, as Mark as shipped lists it. */
export function stillToLoadEntries(view) {
  const entries = [];
  pick(view, false).forEach((order) => {
    order.boxes.forEach((box) => entries.push(`${order.orderNo} · ${boxName(box)}`));
    order.items.forEach((item) => entries.push(`${order.orderNo} · ${item.product || 'line'} (no box code)`));
  });
  return entries;
}

/**
 * True when Mark as shipped must ask first: someone scanned (or ticked) on this shipment and something is still to
 * load. A shipment nobody scanned ships exactly as before, without a question.
 */
export function needsShipConfirmation(view) {
  if (!view || view.closed || !view.scanStarted) return false;
  return stillToLoadEntries(view).length > 0;
}

/**
 * The load after a scan, before the refetch lands: a LOADED box moves to Loaded at once, and the counts follow the
 * scan's answer. Any other result leaves the boxes as they were.
 */
export function applyShipmentScan(view, scan) {
  if (!view || !scan) return view;
  const counts = {
    loaded: Number.isFinite(scan.loaded) ? scan.loaded : view.loaded,
    total: Number.isFinite(scan.total) ? scan.total : view.total,
  };
  if (scan.result !== 'LOADED' || !scan.box?.code) return { ...view, ...counts };
  const orders = (view.orders || []).map((order) => ({
    ...order,
    boxes: (order.boxes || []).map((box) => (box.code === scan.box.code
      ? { ...box, loaded: true, loadedAt: scan.box.loadedAt ?? box.loadedAt }
      : box)),
  }));
  return { ...view, ...counts, orders, scanStarted: true };
}

/** "Box 3", or a stock line's piece (PGSS-5798): "Piece 2 of 6". */
export function boxName(box) {
  return `${box?.piece ? 'Piece' : 'Box'} ${box?.boxNumber || '-'}`;
}

/** One box as listed: "Box 3 · 30412-S1" plus the code. */
export function boxLine(box) {
  return {
    title: boxName(box),
    label: box?.lineOrderNo && box.lineOrderNo !== box.orderNo ? box.lineOrderNo : '',
    code: box?.displayCode || displayCode(box?.code),
  };
}

/** Why a shipment's load could not be opened, in words for the user. */
export function loadErrorText(error) {
  const status = error?.response?.status ?? error?.status;
  const serverMessage = error?.response?.data?.message;
  if (status === 403) return 'Only PSG staff can load a customer shipment.';
  if (status === 400 && serverMessage) return serverMessage;
  return serverMessage || error?.message || 'The shipment could not be loaded.';
}

/**
 * Asked by Mark as shipped before it sends: what is still to load, when the shipment needs a confirmation, or [] to
 * ship as before. `get` is axios.get. If the load can't be read, it ships as before and the server still checks.
 */
export async function stillToLoadBeforeShipping(get, shipmentId) {
  try {
    const { data } = await get(shipmentLoadApi(shipmentId), { timeout: REQUEST_TIMEOUT_MS });
    return needsShipConfirmation(data) ? stillToLoadEntries(data) : [];
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------------------------------------------
// PGSS-5801: take a missing or broken item off a customer shipment at Mark as shipped.

/** Every order line on a customer shipment, with how much of it was loaded (GET .../load/lines). */
export function shipLinesApi(shipmentId) {
  return `${shipmentLoadApi(shipmentId)}/lines`;
}

/** A line's load, as the server works it out from the scans. */
export const SHIP_LINE_STATES = {
  LOADED: { label: 'Loaded', tone: 'success' },
  PARTLY: { label: 'Partly loaded', tone: 'warning' },
  NOT_LOADED: { label: 'Not loaded', tone: 'error' },
  NOT_SCANNED: { label: '', tone: 'neutral' },
};

/** Why an item comes off, as the server takes it. */
export const TAKE_OFF_REASONS = [
  { value: 'MISSING', label: 'Missing' },
  { value: 'BROKEN', label: 'Broken' },
  { value: 'OTHER', label: 'Other' },
];

/** The shipment's lines, or null when they can't be read (Mark as shipped then works as before). */
export async function fetchShipLines(get, shipmentId) {
  try {
    const { data } = await get(shipLinesApi(shipmentId), { timeout: REQUEST_TIMEOUT_MS });
    return Array.isArray(data?.orders) && data.orders.every((order) => Array.isArray(order?.lines)) ? data : null;
  } catch {
    return null;
  }
}

/** "India Black Die × 2", for lists. */
export function shipLineLabel(line) {
  const product = line?.product || 'Item';
  return line?.quantity ? `${product} × ${line.quantity}` : product;
}

/** "1 of 2 boxes loaded" for a partly loaded boxed line ("pieces" with piece labels, PGSS-5798); '' otherwise. */
export function shipLineBoxes(line) {
  return line?.state === 'PARTLY' && line?.boxes ? `${line.boxesLoaded || 0} of ${line.boxes} ${unitsOf(line)} loaded` : '';
}

function unitsOf(line) {
  return line?.pieces ? 'pieces' : 'boxes';
}

/** The quantity to take off as a whole number within the line, or null when it isn't one. */
function takeOffQuantity(entry, line) {
  const raw = entry?.quantity === '' || entry?.quantity === undefined || entry?.quantity === null ? line.quantity : entry.quantity;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 && n <= (line.quantity || 0) ? n : null;
}

/**
 * PGSS-5801 (review of #2777): what still ships of a line with `taken` pieces taken off but not loaded, in words, or ''.
 * The server's rule: a line with one box per piece excuses as many unscanned boxes as pieces taken off; any other line
 * is checked as if nothing were taken off. A line taken off whole ships nothing.
 */
function unloadedShipping(line, taken) {
  const quantity = line?.quantity || 0;
  if (!taken || taken >= quantity) return '';
  if (line.state !== 'NOT_LOADED' && line.state !== 'PARTLY') return '';
  const boxes = line.boxes || 0;
  if (boxes > 0 && boxes === quantity) {
    const notLoaded = quantity - taken - (line.boxesLoaded || 0);
    return notLoaded > 0 ? `${notLoaded} of the ${quantity - taken} still shipping not loaded` : '';
  }
  return boxes > 0 ? `${line.boxesLoaded || 0} of ${boxes} ${unitsOf(line)} loaded` : 'not loaded';
}

/**
 * What Mark as shipped will do with these items taken off:
 * - `takenOff`: sent to the server as taken_off;
 * - `splitOrders`: orders shipping the rest (what is taken off moves to a new "-S" order);
 * - `backToReady`: orders with everything taken off (they leave the shipment);
 * - `nothingShips`: everything taken off, which the server refuses;
 * - `problems`: what must be fixed first (a quantity out of range, Other without a note);
 * - `shippingUnloaded`: items still on the shipment that the scan says are not (fully) loaded, which need the
 *   "ship anyway" question.
 */
export function takeOffPlan(view, takeOff) {
  const chosen = takeOff || {};
  const plan = { takenOff: [], splitOrders: [], backToReady: [], nothingShips: false, problems: [], shippingUnloaded: [] };
  const orders = view?.orders || [];
  let piecesShipping = 0;
  orders.forEach((order) => {
    const lines = order.lines || [];
    let piecesOff = 0;
    let piecesOnOrder = 0;
    lines.forEach((line) => {
      const entry = chosen[line.orderDetailId];
      piecesOnOrder += line.quantity || 0;
      if (entry) {
        const quantity = takeOffQuantity(entry, line);
        const note = String(entry.note || '').trim();
        if (quantity === null) plan.problems.push(`${order.orderNo} · ${line.product || 'Item'}: take off between 1 and ${line.quantity}.`);
        if (entry.reason === 'OTHER' && !note) plan.problems.push(`${order.orderNo} · ${line.product || 'Item'}: add a note for Other.`);
        const taken = quantity ?? 0;
        piecesOff += taken;
        plan.takenOff.push({ order_detail_id: line.orderDetailId, quantity: taken, reason: entry.reason, ...(note ? { note } : {}) });
        // Review of #2777: part of a line taken off still ships the rest, which may not all be loaded.
        const unloaded = unloadedShipping(line, taken);
        if (unloaded) plan.shippingUnloaded.push(`${order.orderNo} · ${line.product || 'Item'}: ${unloaded}`);
        return;
      }
      if (line.state === 'NOT_LOADED' || line.state === 'PARTLY') {
        const boxes = shipLineBoxes(line);
        plan.shippingUnloaded.push(`${order.orderNo} · ${shipLineLabel(line)}${boxes ? ` (${boxes})` : ' (not loaded)'}`);
      }
    });
    piecesShipping += piecesOnOrder - piecesOff;
    if (piecesOff > 0 && piecesOff === piecesOnOrder) plan.backToReady.push(order.orderNo);
    else if (piecesOff > 0) plan.splitOrders.push(order.orderNo);
  });
  plan.nothingShips = orders.length > 0 && piecesShipping <= 0;
  return plan;
}

/** One sentence on what taking these off does, shown under the list; '' when nothing is taken off. */
export function takeOffSummary(plan) {
  if (!plan || !plan.takenOff.length) return '';
  const n = plan.takenOff.length;
  const parts = [];
  if (plan.splitOrders.length) {
    parts.push(`${plan.splitOrders.join(', ')} ${plan.splitOrders.length === 1 ? 'ships' : 'ship'} the rest; what is taken off moves to a new order at Ready for Shipment`);
  }
  if (plan.backToReady.length) {
    parts.push(`${plan.backToReady.join(', ')} ${plan.backToReady.length === 1 ? 'goes' : 'go'} back to Ready for Shipment`);
  }
  return `${n} ${n === 1 ? 'item' : 'items'} taken off: ${parts.join('. ')}.`;
}

/** The success message after Mark as shipped, naming where the items taken off went (the server's answer). */
export function markedShippedMessage(data) {
  const split = Array.isArray(data?.split_orders) ? data.split_orders : [];
  const off = Array.isArray(data?.taken_off_orders) ? data.taken_off_orders : [];
  if (!split.length && !off.length) return 'Shipment is completed and marked as shipped';
  const notes = [];
  if (split.length) notes.push(`Items taken off are on ${split.join(', ')}`);
  if (off.length) notes.push(`${off.join(', ')} ${off.length === 1 ? 'is' : 'are'} back at Ready for Shipment`);
  return `Shipment marked as shipped. ${notes.join('. ')}.`;
}
